import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api-service";
import { Invoice } from "../../services/api";

interface InvoiceListProps {
  trigger: boolean;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ trigger }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!user?.username) return;

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getInvoiceList(user.username);
        setInvoices(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [user?.username, t, trigger]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" => {
    switch (status.toUpperCase()) {
      case "PROCESSED":
        return "success";
      case "REJECTED":
      case "FAILED":
      case "ERROR":
        return "error";
      default:
        return "warning";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="25%">{t("invoice.dateSubmitted")}</TableCell>
            <TableCell width="25%">{t("common.status")}</TableCell>
            <TableCell width="50%">{t("invoice.fileName")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                {t("invoice.noInvoices")}
              </TableCell>
            </TableRow>
          ) : (
            invoices.map((invoice, index) => (
              <TableRow key={index}>
                <TableCell>{formatDate(invoice.dateSubmitted)}</TableCell>
                <TableCell>
                  <Chip
                    label={invoice.status}
                    color={getStatusColor(invoice.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    gap={1}
                    sx={{ width: "100%" }}
                  >
                    <Tooltip title={invoice.fileName} enterDelay={500}>
                      <Chip
                        label={invoice.fileName}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "none",
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InvoiceList;
